
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// xlsx (~50 kB gzipped) is lazy-loaded inside the export handler so
// non-admin users don't pay for it at app load. CRA doesn't route-split
// by default, so a top-level import would land in the main bundle.
import { supabase } from '../lib/api';
import { useUser } from '../context/UserContext';
import { showError } from '../lib/toast';
import { ANN_TAG_NAMES, annTagColor } from '../lib/announcementTags';

const B = {
  black: '#1e1e23', surface: '#26262d', surfaceLight: '#2e2e36',
  border: '#1e1a2e', borderLight: '#2e2850',
  purple: '#7B5CC8', purpleDim: '#4a3880', purpleDeep: '#1a1030',
  gold: '#C9A84C', goldDim: '#8a6e2a',
  textPrimary: '#ffffff', textSecondary: '#8888aa', textMuted: '#444466',
  green: '#1D9E75', greenDeep: '#0a1f15', greenDim: '#0f6e56',
  red: '#c0392b', redDeep: '#1a0508', redDim: '#7a1a1a',
};

const RATINGS = {
  1: { label: 'Unsatisfactory', color: '#c0392b' },
  2: { label: 'Needs Improvement', color: '#C9A84C' },
  3: { label: 'Solid & Reliable', color: '#7B5CC8' },
  4: { label: 'Strong Performer', color: '#1D9E75' },
  5: { label: 'Outstanding', color: '#C9A84C' },
};

const ROLES = ['Employee', 'Manager', 'Admin'];

const roleMeta = {
  Employee: { color: B.textSecondary, bg: B.surfaceLight, border: B.border },
  Manager: { color: B.gold, bg: '#0f0d05', border: B.goldDim },
  Admin: { color: B.purple, bg: B.purpleDeep, border: B.purpleDim },
};

const statusMeta = {
  not_started: { label: 'Not Started', color: B.textMuted, bg: B.surfaceLight, border: B.border },
  in_progress: { label: 'In Progress', color: B.gold, bg: '#0f0d05', border: B.goldDim },
  submitted: { label: 'Submitted', color: B.purple, bg: B.purpleDeep, border: B.purpleDim },
  completed: { label: 'Completed', color: B.green, bg: B.greenDeep, border: B.greenDim },
  // Reviews-editor manager_status uses 'pending' as its initial value;
  // styled neutral so it reads as "no decision yet" rather than red/green.
  pending: { label: 'Pending', color: B.textMuted, bg: B.surfaceLight, border: B.border },
};

const initials = n => n?.split(' ').map(x => x[0]).join('') || '?';

// Mirrors the tag set on HRPortal so the colors match what employees see.
const EMPTY_ANN = { title: '', body: '', tag: 'General', pinned: false };

// Cycle validation — kept here so addCycle and saveEditCycle stay in sync.
const MIN_CYCLE_YEAR = 2020;
const MAX_CYCLE_YEAR = 2100;
const validateCycleFields = ({ label, year, type }) => {
  const errs = {};
  const trimmed = (label || '').trim();
  const yr = parseInt(year, 10);
  if (!trimmed) errs.label = 'Required';
  if (!yr || yr < MIN_CYCLE_YEAR || yr > MAX_CYCLE_YEAR) errs.year = `Year must be ${MIN_CYCLE_YEAR}–${MAX_CYCLE_YEAR}`;
  if (type !== undefined && !['Annual', 'Mid-Year'].includes(type)) errs.type = 'Pick a type';
  return { errs, label: trimmed, year: yr };
};

export default function AdminDashboard() {
  const { employee: admin } = useUser();
  const navigate = useNavigate();
  const [view, setView] = useState('dashboard');
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [annualReviews, setAnnualReviews] = useState([]);
  const [midyearReviews, setMidyearReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveMsg, setSaveMsg] = useState('');

  // Export tool state
  const [exportCycleId, setExportCycleId] = useState('');
  const [exportFormat, setExportFormat] = useState('csv');

  // Import tool state. The summary modal stays open until dismissed so
  // the admin can see which rows were skipped and why before retrying.
  const [importCycleId, setImportCycleId] = useState('');
  const [importType, setImportType] = useState('Annual');
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  // The <input type="file"> is uncontrolled — clearing importFile state
  // alone won't reset the chooser UI or let the user re-select the same
  // file, so we reach in and zero input.value via this ref.
  const importInputRef = useRef(null);
  const resetImportFile = () => {
    setImportFile(null);
    if (importInputRef.current) importInputRef.current.value = '';
  };

  // People & Access state — read-only view of Okta-sourced employees,
  // except for role and review manager (both myhr-internal: role is the
  // myhr access tier, review_manager_email overrides who reviews this
  // employee without changing the Okta manager-of-record).
  const [showInactive, setShowInactive] = useState(false);
  const [searchPeople, setSearchPeople] = useState('');
  const [editingRole, setEditingRole] = useState(null);
  const [editingReviewMgr, setEditingReviewMgr] = useState(null);

  // Announcements state — list, add/edit form, and delete confirm. The
  // departments fetch is kept (Dashboard deptStats and the People form
  // depend on it), but admin CRUD on departments has moved off the UI;
  // announcements take the freed-up tab slot.
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnForm, setShowAnnForm] = useState(false);
  const [editingAnn, setEditingAnn] = useState(null);
  const [annDraft, setAnnDraft] = useState(EMPTY_ANN);
  const [annErrors, setAnnErrors] = useState({});
  const [confirmDeleteAnn, setConfirmDeleteAnn] = useState(null);

  // Dashboard filters
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [confirmCycle, setConfirmCycle] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Cycles tab state
  const [showAddCycle, setShowAddCycle] = useState(false);
  const [newCycle, setNewCycle] = useState({ label: '', type: 'Annual', year: new Date().getFullYear() });
  const [cycleErrors, setCycleErrors] = useState({});
  const [editingCycle, setEditingCycle] = useState(null);
  const [editCycleValues, setEditCycleValues] = useState({ label: '', year: '' });
  const [confirmDeleteCycle, setConfirmDeleteCycle] = useState(null);

  // Audit log state
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Sync Issues — populated by the Okta sync (see sync_errors table). The
  // sync truncates and rewrites this table each run, so the list reflects
  // the latest sync's findings. Empty = clean.
  const [syncIssues, setSyncIssues] = useState([]);
  const [syncIssuesLoading, setSyncIssuesLoading] = useState(false);
  // Separate "have we loaded yet?" flag so the lazy-load guard doesn't
  // re-fetch every time the user switches to this tab when there are
  // legitimately zero issues. Manual REFRESH still works as before.
  const [syncIssuesLoaded, setSyncIssuesLoaded] = useState(false);
  const [auditExpanded, setAuditExpanded] = useState(null);

  // Reviews-editor state. Two pickers (employee + cycle) load the row
  // (annual or midyear, keyed by cycle.type) into editForm; save upserts.
  // Includes inactive employees so leavers' historical reviews stay
  // editable. reviewDraft is a flat snapshot of the row's fields plus
  // the status enums, so the form can stay dumb-controlled.
  const [revEmployeeId, setRevEmployeeId] = useState('');
  const [revCycleId, setRevCycleId] = useState('');
  const [revEmpSearch, setRevEmpSearch] = useState('');
  const [reviewDraft, setReviewDraft] = useState(null);   // row currently in form
  const [reviewExists, setReviewExists] = useState(false); // false = INSERT on save
  const [revLoading, setRevLoading] = useState(false);
  const [revSaving, setRevSaving] = useState(false);

  const flash = msg => { setSaveMsg(msg); setTimeout(() => setSaveMsg(''), 3000); };

  useEffect(() => { fetchAll(); }, []);

  // Lazy-load tab data on entry: audit log fetches on view==='audit',
  // sync issues fetch on view==='sync-issues'. Reset audit's expand state
  // when leaving that tab. Declared at the top with the other hooks so
  // React's rules-of-hooks is happy (we have early returns below per tab).
  useEffect(() => {
    if (view === 'audit' && !auditEntries.length && !auditLoading) {
      fetchAuditLog();
    }
    if (view !== 'audit' && auditExpanded) {
      setAuditExpanded(null);
    }
    if (view === 'sync-issues' && !syncIssuesLoaded && !syncIssuesLoading) {
      fetchSyncIssues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      // midyear_reviews stays narrowed to id,cycle_id here — only the
      // cycle-delete safeguard counts need it. The export tool fetches
      // full review rows for the selected cycle on demand (see
      // handleExportSubmit) so we don't load every historical mid-year
      // review on every admin load.
      // announcements are sorted pinned-first then newest-first to match
      // the order HRPortal renders them — admins see the same list.
      const [{ data: emps }, { data: depts }, { data: cycs }, { data: annual }, { data: midyear }, { data: anns }] = await Promise.all([
        supabase.from('employees').select('*').order('name'),
        supabase.from('departments').select('*').order('name'),
        supabase.from('review_cycles').select('*').order('year', { ascending: false }),
        supabase.from('annual_reviews').select('*'),
        supabase.from('midyear_reviews').select('id,cycle_id'),
        supabase.from('announcements').select('*').order('pinned', { ascending: false }).order('created_at', { ascending: false }),
      ]);
      setEmployees(emps || []);
      setDepartments((depts || []).map(d => d.name));
      setCycles(cycs || []);
      setAnnualReviews(annual || []);
      setMidyearReviews(midyear || []);
      setAnnouncements(anns || []);
    } catch (err) {
      // api.js will already have surfaced a toast; this is a hard
      // belt-and-suspenders so the loading screen doesn't get stuck.
      console.error('fetchAll failed:', err);
      flash('Failed to load dashboard data. Try refreshing.');
    } finally {
      setLoading(false);
    }
  };

  const activeEmps = employees.filter(e => e.active);
  const inactiveEmps = employees.filter(e => !e.active);
  // Eligible reviewers for the review_manager_email override. Limit to
  // active Manager/Admin roles so admins can't accidentally pick an IC.
  const reviewerOptions = activeEmps
    .filter(e => e.role === 'Manager' || e.role === 'Admin')
    .sort((a, b) => a.name.localeCompare(b.name));

  // ── Cycle controls ──────────────────────────────────────────────────────────
  const toggleCycle = async id => {
    if (confirmCycle !== id) { setConfirmCycle(id); return; }
    const cycle = cycles.find(c => c.id === id);

    // DB has a partial unique index "one open per type"; check client-side
    // first for a friendlier message than the raw constraint violation.
    if (!cycle.is_open) {
      const conflict = cycles.find(c => c.id !== id && c.type === cycle.type && c.is_open);
      if (conflict) {
        flash(`Close "${conflict.label}" first — only one ${cycle.type} cycle can be open at a time.`);
        setConfirmCycle(null);
        return;
      }
    }

    const { error } = await supabase.from('review_cycles').update({
      is_open: !cycle.is_open,
      ...(cycle.is_open ? { closed_at: new Date().toISOString() } : { opened_at: new Date().toISOString() }),
    }).eq('id', id);

    if (error) {
      // Belt-and-suspenders: catch the partial-unique-index violation if a
      // race somehow slipped past the client check.
      if (error.code === '23505') {
        flash(`Another ${cycle.type} cycle is already open. Close it first.`);
      } else {
        flash(error.message || 'Could not update cycle');
      }
      setConfirmCycle(null);
      return;
    }
    fetchAll();
    flash(cycle.is_open ? 'Cycle closed' : 'Cycle opened');
    setConfirmCycle(null);
  };

  // Pre-compute review counts per cycle so render is O(reviews) once
  // instead of O(cycles × reviews) on each render.
  const reviewCountByCycleId = useMemo(() => {
    const map = new Map();
    for (const r of annualReviews) map.set(r.cycle_id, (map.get(r.cycle_id) || 0) + 1);
    for (const r of midyearReviews) map.set(r.cycle_id, (map.get(r.cycle_id) || 0) + 1);
    return map;
  }, [annualReviews, midyearReviews]);
  const cycleHasReviews = id => (reviewCountByCycleId.get(id) || 0) > 0;

  const addCycle = async () => {
    const { errs, label, year } = validateCycleFields(newCycle);
    if (Object.keys(errs).length) { setCycleErrors(errs); return; }

    const { error } = await supabase.from('review_cycles').insert({
      label, type: newCycle.type, year, is_open: false,
    });
    if (error) { flash(error.message || 'Could not add cycle'); return; }
    setNewCycle({ label: '', type: 'Annual', year: new Date().getFullYear() });
    setCycleErrors({});
    setShowAddCycle(false);
    fetchAll();
    flash(`${label} added`);
  };

  const startEditCycle = c => {
    setEditingCycle(c.id);
    setEditCycleValues({ label: c.label, year: String(c.year) });
  };

  const saveEditCycle = async id => {
    const { errs, label, year } = validateCycleFields(editCycleValues);
    if (Object.keys(errs).length) { flash(errs.label || errs.year || 'Invalid input'); return; }

    const { error } = await supabase.from('review_cycles').update({ label, year }).eq('id', id);
    if (error) { flash(error.message || 'Could not update cycle'); return; }
    fetchAll();
    flash('Cycle updated');
    setEditingCycle(null);
  };

  const deleteCycle = async id => {
    if (cycleHasReviews(id)) {
      flash('Cannot delete: this cycle has reviews. Close it instead.');
      setConfirmDeleteCycle(null);
      return;
    }
    if (confirmDeleteCycle === id) {
      const { error } = await supabase.from('review_cycles').delete().eq('id', id);
      if (error) {
        // The DB has the same safeguard via RLS — surface it gracefully.
        const msg = (error.message || '').toLowerCase();
        flash(msg.includes('policy') || msg.includes('foreign key')
          ? 'Cannot delete: this cycle has reviews. Close it instead.'
          : (error.message || 'Could not delete cycle'));
        setConfirmDeleteCycle(null);
        return;
      }
      fetchAll();
      flash('Cycle deleted');
      setConfirmDeleteCycle(null);
    } else {
      setConfirmDeleteCycle(id);
    }
  };

  // ── Employee role assignment ────────────────────────────────────────────────
  // Name, email, department, manager, and active state come from the daily
  // Okta sync. Role and review_manager_email are myhr-internal — once HR
  // sets them here, the sync's UPSERT leaves them alone.
  const updateRole = async (id, role) => {
    await supabase.from('employees').update({ role }).eq('id', id);
    setEditingRole(null); fetchAll();
    flash(`Role updated to ${role}`);
  };

  // Empty string from the dropdown means "(default — Okta manager)" and
  // clears the override; non-empty values are emails of active managers.
  const updateReviewManager = async (id, email) => {
    const value = email ? email : null;
    // .silent() suppresses the QueryBuilder's global red toast on non-2xx
    // so this handler can show its own contextual message exactly once.
    // flash() always renders green; route errors through showError (red).
    const { error } = await supabase
      .from('employees')
      .update({ review_manager_email: value })
      .eq('id', id)
      .silent();
    setEditingReviewMgr(null);
    if (error) { showError(error.message || 'Could not update review manager'); return; }
    fetchAll();
    flash(value ? 'Review manager updated' : 'Review manager reset to Okta default');
  };

  // ── Announcement management ─────────────────────────────────────────────────
  const validateAnn = draft => {
    const errs = {};
    if (!draft.title.trim()) errs.title = 'Required';
    if (!draft.body.trim()) errs.body = 'Required';
    if (!ANN_TAG_NAMES.includes(draft.tag)) errs.tag = 'Pick a tag';
    return errs;
  };

  // Clear confirmDeleteAnn whenever the form opens or closes, otherwise a
  // CONFIRM-DELETE state can outlive the form and a user editing one card
  // could trigger a delete on another that's still showing the prompt.
  const startNewAnn = () => {
    setEditingAnn(null);
    setAnnDraft(EMPTY_ANN);
    setAnnErrors({});
    setConfirmDeleteAnn(null);
    setShowAnnForm(true);
  };

  const startEditAnn = a => {
    setEditingAnn(a.id);
    setAnnDraft({ title: a.title, body: a.body, tag: a.tag, pinned: !!a.pinned });
    setAnnErrors({});
    setConfirmDeleteAnn(null);
    setShowAnnForm(true);
  };

  const cancelAnnForm = () => {
    setShowAnnForm(false);
    setEditingAnn(null);
    setAnnDraft(EMPTY_ANN);
    setAnnErrors({});
    setConfirmDeleteAnn(null);
  };

  const saveAnn = async () => {
    const errs = validateAnn(annDraft);
    if (Object.keys(errs).length) { setAnnErrors(errs); return; }
    const payload = {
      title: annDraft.title.trim(),
      body: annDraft.body.trim(),
      tag: annDraft.tag,
      pinned: !!annDraft.pinned,
    };

    // flash() always renders green, so it can't carry failure messages —
    // route errors through the global toast (red) and keep flash for
    // successes only. Same pattern below in deleteAnn.
    if (editingAnn) {
      // updated_at is maintained by the announcements_updated_at trigger.
      const { error } = await supabase.from('announcements').update(payload).eq('id', editingAnn);
      if (error) { showError(error.message || 'Could not save announcement'); return; }
      flash('Announcement updated');
    } else {
      // Stamp published_by with the current admin's employee id so the
      // record links back to who posted it (FK is ON DELETE SET NULL).
      const { error } = await supabase.from('announcements').insert({ ...payload, published_by: admin?.id || null });
      if (error) { showError(error.message || 'Could not publish announcement'); return; }
      flash('Announcement published');
    }
    cancelAnnForm();
    fetchAll();
  };

  const deleteAnn = async id => {
    if (confirmDeleteAnn !== id) { setConfirmDeleteAnn(id); return; }
    const { error } = await supabase.from('announcements').delete().eq('id', id);
    setConfirmDeleteAnn(null);
    if (error) { showError(error.message || 'Could not delete announcement'); return; }
    fetchAll();
    flash('Announcement deleted');
  };

  // ── Reviews editor ──────────────────────────────────────────────────────────
  // Empty draft shapes match the column set on each table — kept here so
  // the UI never has to special-case "row doesn't exist yet" vs. "loaded".
  const EMPTY_ANNUAL = {
    achievements: '', skills: '', must_do: '', should_do: '', could_do: '',
    manager_feedback: '', rating: null,
    emp_status: 'not_started', manager_status: 'pending',
  };
  const EMPTY_MIDYEAR = {
    must_status: null, must_update: '',
    should_status: null, should_update: '',
    could_status: null, could_update: '',
    next_must: '', next_should: '', next_could: '',
    manager_comments: '',
    emp_status: 'not_started', manager_status: 'pending',
  };

  // Pull the existing review for emp+cycle, or seed an empty draft so the
  // form renders. reviewExists drives INSERT vs. UPDATE on save.
  const loadReview = async (employeeId, cycleId) => {
    if (!employeeId || !cycleId) { setReviewDraft(null); setReviewExists(false); return; }
    const cycle = cycles.find(c => c.id === cycleId);
    if (!cycle) return;
    setRevLoading(true);
    try {
      const table = cycle.type === 'Annual' ? 'annual_reviews' : 'midyear_reviews';
      const { data, error } = await supabase
        .from(table).select('*')
        .eq('employee_id', employeeId)
        .eq('cycle_id', cycleId)
        .silent();
      if (error) {
        // Clear stale draft so the form can't display or save data from
        // a previously-loaded employee+cycle on top of a failed fetch.
        setReviewDraft(null); setReviewExists(false);
        showError(error.message || 'Failed to load review');
        return;
      }
      const existing = Array.isArray(data) ? data[0] : data;
      if (existing) {
        setReviewDraft(existing);
        setReviewExists(true);
      } else {
        setReviewDraft(cycle.type === 'Annual' ? { ...EMPTY_ANNUAL } : { ...EMPTY_MIDYEAR });
        setReviewExists(false);
      }
    } finally {
      setRevLoading(false);
    }
  };

  // Whenever both pickers settle, (re)fetch. Keep cycle change resilient:
  // an Annual review draft for one cycle can't be reused for a Mid-Year one.
  useEffect(() => {
    if (view === 'reviews' && revEmployeeId && revCycleId) {
      loadReview(revEmployeeId, revCycleId);
    } else if (!revEmployeeId || !revCycleId) {
      setReviewDraft(null); setReviewExists(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, revEmployeeId, revCycleId]);

  const setRevField = (k, v) => setReviewDraft(d => ({ ...d, [k]: v }));

  const saveReview = async () => {
    if (!reviewDraft || !revEmployeeId || !revCycleId) return;
    const cycle = cycles.find(c => c.id === revCycleId);
    if (!cycle) return;
    const table = cycle.type === 'Annual' ? 'annual_reviews' : 'midyear_reviews';
    setRevSaving(true);
    try {
      // Strip server-managed fields on UPDATE so we don't try to write
      // id/created_at/updated_at back. INSERT seeds employee_id + cycle_id.
      const { id, created_at, updated_at, employee_id: _e, cycle_id: _c, ...editable } = reviewDraft;
      // Mirror the timestamp behavior of EmployeeSelfEval/ManagerReview
      // (and their Mid-Year twins) so admin status flips don't desync the
      // audit/export columns. Stamp on advance to a completion-style status
      // when the existing timestamp is empty; never clear on revert — admins
      // walking a row backwards shouldn't erase history.
      const now = new Date().toISOString();
      if (editable.emp_status === 'submitted' && !editable.submitted_at) {
        editable.submitted_at = now;
      }
      if (editable.manager_status === 'completed' && !editable.manager_reviewed_at) {
        editable.manager_reviewed_at = now;
      }
      // .silent() suppresses the api client's global error toast so the
      // explicit showError below isn't duplicated by it.
      if (reviewExists) {
        const { error } = await supabase.from(table).update(editable).eq('id', id).silent();
        if (error) { showError(error.message || 'Could not save review'); return; }
        flash('Review saved');
      } else {
        const payload = { ...editable, employee_id: revEmployeeId, cycle_id: revCycleId };
        const { data, error } = await supabase.from(table).insert(payload).select().single().silent();
        if (error) { showError(error.message || 'Could not create review'); return; }
        if (data) { setReviewDraft(data); setReviewExists(true); }
        flash('Review created');
      }
      // Refresh the dashboard's cached review summaries so progress
      // counters reflect any status flip an admin just made.
      fetchAll();
    } finally {
      setRevSaving(false);
    }
  };

  // ── Export ──────────────────────────────────────────────────────────────────
  // Build export rows for any cycle (Annual or Mid-Year). Includes status,
  // rating, all review answers, plus employee + cycle metadata.
  //
  // Includes both active and deactivated employees — leavers may have
  // completed reviews from past cycles that HR needs for the record. The
  // "Active" column lets the consumer filter if they want.
  const buildExportRows = (cycle, cycleReviewsForExport) => {
    const ratingLabel = r => r ? `${r} - ${RATINGS[r]?.label}` : '';
    const fmtDate = v => v ? new Date(v).toLocaleDateString() : '';
    const overallStatus = (rev) => {
      if (!rev) return 'Not Started';
      if (rev.manager_status === 'completed') return 'Completed';
      if (rev.emp_status === 'submitted')     return 'Submitted';
      if (rev.emp_status === 'in_progress')   return 'In Progress';
      return 'Not Started';
    };

    // Pre-index the (already cycle-scoped) reviews by employee_id for O(1)
    // lookups while building the rows.
    const reviewsByEmp = new Map();
    for (const r of cycleReviewsForExport) reviewsByEmp.set(r.employee_id, r);

    if (cycle.type === 'Annual') {
      return employees.map(e => {
        const r = reviewsByEmp.get(e.id);
        return {
          'Cycle': cycle.label,
          'Cycle Type': cycle.type,
          'Cycle Year': cycle.year,
          'Name': e.name,
          'Email': e.email,
          'Department': e.department,
          'Manager': e.manager_name || '',
          'Role': e.role,
          'Active': e.active ? 'Yes' : 'No',
          'Status': overallStatus(r),
          'Submitted At': fmtDate(r?.submitted_at),
          'Manager Reviewed At': fmtDate(r?.manager_reviewed_at),
          'Rating': ratingLabel(r?.rating),
          'Key Achievements & Learnings': r?.achievements || '',
          'Skills & Development': r?.skills || '',
          'Must Do': r?.must_do || '',
          'Should Do': r?.should_do || '',
          'Could Do': r?.could_do || '',
          'Manager Feedback': r?.manager_feedback || '',
        };
      });
    }
    // Mid-Year
    return employees.map(e => {
      const r = reviewsByEmp.get(e.id);
      return {
        'Cycle': cycle.label,
        'Cycle Type': cycle.type,
        'Cycle Year': cycle.year,
        'Name': e.name,
        'Email': e.email,
        'Department': e.department,
        'Manager': e.manager_name || '',
        'Role': e.role,
        'Active': e.active ? 'Yes' : 'No',
        'Status': overallStatus(r),
        'Submitted At': fmtDate(r?.submitted_at),
        'Manager Reviewed At': fmtDate(r?.manager_reviewed_at),
        'Must Status': r?.must_status || '',
        'Must Update': r?.must_update || '',
        'Should Status': r?.should_status || '',
        'Should Update': r?.should_update || '',
        'Could Status': r?.could_status || '',
        'Could Update': r?.could_update || '',
        'Next Must': r?.next_must || '',
        'Next Should': r?.next_should || '',
        'Next Could': r?.next_could || '',
        'Manager Comments': r?.manager_comments || '',
      };
    });
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportSubmit = async () => {
    if (!exportCycleId) { flash('Pick a cycle first'); return; }
    const cycle = cycles.find(c => c.id === exportCycleId);
    if (!cycle) { flash('Cycle not found'); return; }
    setExporting(true);
    try {
      // Fetch reviews on demand for just the selected cycle. Avoids
      // pulling all historical mid-year/annual reviews into memory at
      // dashboard load just to support an occasional export.
      const table = cycle.type === 'Annual' ? 'annual_reviews' : 'midyear_reviews';
      const { data: cycleReviews, error: reviewErr } = await supabase
        .from(table)
        .select('*')
        .eq('cycle_id', cycle.id);
      if (reviewErr) { flash('Failed to load review data'); return; }

      const rows = buildExportRows(cycle, cycleReviews || []);
      const safeLabel = cycle.label.replace(/[^a-zA-Z0-9]+/g, '_');
      const baseName = `xLight_${cycle.type}_${safeLabel}`;

      if (exportFormat === 'csv') {
        const headers = Object.keys(rows[0] || {});
        const escape = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
        // U+FEFF BOM + CRLF for Excel-on-Windows compatibility: without
        // the BOM, Excel mis-detects encoding for non-ASCII names; CRLF
        // avoids multi-line cell breakage in Excel's CSV import.
        // Use the \ufeff escape (not a literal U+FEFF) so the intent is
        // visible in the source and editors/formatters don't strip it.
        const BOM = '\ufeff';
        const csv = BOM + [headers.map(escape).join(','), ...rows.map(r => headers.map(h => escape(r[h])).join(','))].join('\r\n');
        downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `${baseName}.csv`);
      } else if (exportFormat === 'xlsx') {
        // Lazy-load SheetJS only when admin actually clicks Excel export.
        // Webpack code-splits this into its own chunk, off the main bundle.
        const xlsxMod = await import('xlsx');
        const XLSX = xlsxMod.default || xlsxMod;
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, cycle.type);
        const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
        downloadBlob(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${baseName}.xlsx`);
      } else if (exportFormat === 'json') {
        const payload = { cycle: { id: cycle.id, label: cycle.label, type: cycle.type, year: cycle.year, is_open: cycle.is_open }, exported_at: new Date().toISOString(), rows };
        downloadBlob(new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8;' }), `${baseName}.json`);
      }
      flash(`${rows.length} rows exported`);
    } catch (err) {
      console.error(err); flash('Export failed');
    } finally {
      setExporting(false);
    }
  };

  // ── Import ──────────────────────────────────────────────────────────────────
  // Mirrors buildExportRows: same column headers, reversed. The import never
  // creates new employees — unknown emails are skipped with a warning so HR
  // can fix Okta/People & Access first instead of leaving orphan review rows.

  // Locale-formatted dates from CSV exports parse cleanly via new Date(); ISO
  // (from JSON exports) does too. Anything unparseable becomes null.
  const parseDate = v => {
    if (v === undefined || v === null || v === '') return null;
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d.toISOString();
  };

  // Export writes "4 - Strong Performer"; accept the leading int or a bare 1–5.
  const parseRating = v => {
    if (v === undefined || v === null || v === '') return null;
    const m = String(v).match(/^\s*(\d)/);
    if (!m) return null;
    const n = parseInt(m[1], 10);
    return n >= 1 && n <= 5 ? n : null;
  };

  // Status column is derived in the export; reverse it back to the two
  // underlying flags. Unknown labels return null so the caller can skip
  // the row with a clear reason instead of silently importing as
  // not_started — typos in the spreadsheet would otherwise sneak in.
  const splitStatus = label => {
    switch ((label || '').trim().toLowerCase()) {
      case 'completed':   return { emp_status: 'submitted',   manager_status: 'completed' };
      case 'submitted':   return { emp_status: 'submitted',   manager_status: 'pending'   };
      case 'in progress': return { emp_status: 'in_progress', manager_status: 'pending'   };
      case 'not started': return { emp_status: 'not_started', manager_status: 'pending'   };
      default: return null;
    }
  };

  // Mid-year status enum is nullable in the schema; empty string would fail
  // the CHECK, so coerce blanks to null. Normalize case + spacing so admin
  // edits like "Done" or "In Progress" match. Unknown values return
  // undefined so the caller can skip the row instead of silently dropping
  // data with a NULL.
  const MIDYEAR_STATUSES = new Set(['done', 'in_progress', 'not_started', 'deprioritised']);
  const cleanMidyearStatus = v => {
    const s = (v == null ? '' : String(v)).trim();
    if (!s) return null;
    const norm = s.toLowerCase().replace(/\s+/g, '_');
    return MIDYEAR_STATUSES.has(norm) ? norm : undefined;
  };

  // Read a File into the row array the export produced. JSON wraps rows in
  // { cycle, exported_at, rows }; CSV/XLSX are flat row arrays. xlsx parses
  // CSV too, so one code path covers both binary and text spreadsheets.
  // rowNumberOffset is what to add to the row index when reporting back to
  // the admin: CSV/XLSX have a header on line 1 (so first data row is 2),
  // JSON has no header (so first row is 1).
  const parseImportFile = async file => {
    const name = (file.name || '').toLowerCase();
    if (name.endsWith('.json')) {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const rows = Array.isArray(parsed) ? parsed : (parsed.rows || []);
      return { rows, rowNumberOffset: 1 };
    }
    const xlsxMod = await import('xlsx');
    const XLSX = xlsxMod.default || xlsxMod;
    const buf = await file.arrayBuffer();
    // cellDates parses Excel date serials into JS Date objects; otherwise
    // parseDate would treat the numeric serial as epoch ms and produce a
    // 1970-era timestamp. parseDate handles ISO strings and Dates alike.
    const wb = XLSX.read(buf, { type: 'array', cellDates: true });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
    return { rows, rowNumberOffset: 2 };
  };

  // Row builders take an already-validated split status from the caller so
  // unknown labels don't silently coerce to not_started.
  const buildAnnualRow = (row, employeeId, cycleId, split) => ({
    employee_id: employeeId,
    cycle_id: cycleId,
    achievements:     row['Key Achievements & Learnings'] || null,
    skills:           row['Skills & Development'] || null,
    must_do:          row['Must Do'] || null,
    should_do:        row['Should Do'] || null,
    could_do:         row['Could Do'] || null,
    emp_status:       split.emp_status,
    submitted_at:     parseDate(row['Submitted At']),
    manager_feedback: row['Manager Feedback'] || null,
    rating:           parseRating(row['Rating']),
    manager_status:   split.manager_status,
    manager_reviewed_at: parseDate(row['Manager Reviewed At']),
  });

  // Mid-year statuses are pre-validated by the caller; undefined means
  // "unrecognized" and should already have skipped the row, while null
  // means "not yet set" and is the correct value to write.
  const buildMidyearRow = (row, employeeId, cycleId, split, midStatuses) => ({
    employee_id: employeeId,
    cycle_id: cycleId,
    must_status:    midStatuses.must,
    must_update:    row['Must Update'] || null,
    should_status:  midStatuses.should,
    should_update:  row['Should Update'] || null,
    could_status:   midStatuses.could,
    could_update:   row['Could Update'] || null,
    next_must:      row['Next Must'] || null,
    next_should:    row['Next Should'] || null,
    next_could:     row['Next Could'] || null,
    emp_status:     split.emp_status,
    submitted_at:     parseDate(row['Submitted At']),
    manager_comments: row['Manager Comments'] || null,
    manager_status: split.manager_status,
    manager_reviewed_at: parseDate(row['Manager Reviewed At']),
  });

  const handleImportSubmit = async () => {
    // flash() always renders green (success styling) so it can't carry
    // failures — route validation/error paths through showError (red toast)
    // for consistent error UX.
    if (!importCycleId) { showError('Pick a cycle first'); return; }
    if (!importFile)    { showError('Pick a file');        return; }
    const cycle = cycles.find(c => c.id === importCycleId);
    if (!cycle) { showError('Cycle not found'); return; }

    // Insert table is keyed off cycle.type, not the (display-only) selector
    // — picking an Annual cycle but Mid-Year type would otherwise drop rows
    // into the wrong table. The selector is also kept locked in sync via
    // setImportType when the cycle changes.
    if (cycle.type !== importType) {
      showError(`Cycle is ${cycle.type}; review type must match`);
      return;
    }

    setImporting(true);
    try {
      let rows, rowNumberOffset;
      try {
        ({ rows, rowNumberOffset } = await parseImportFile(importFile));
      } catch (err) {
        showError(`Could not parse file: ${err.message || 'invalid format'}`);
        return;
      }
      if (!Array.isArray(rows) || rows.length === 0) {
        showError('No rows found in file');
        return;
      }

      // Lookup map matches the export's email field exactly; lowercased to
      // sidestep mixed-case rows that may pre-date the schema.sql normalize.
      const empByEmail = new Map(employees.map(e => [String(e.email).toLowerCase(), e]));

      const skipped = [];
      let imported = 0;
      const isAnnual = cycle.type === 'Annual';
      const table = isAnnual ? 'annual_reviews' : 'midyear_reviews';

      // One round-trip per row. Volumes are small (≤ headcount per cycle)
      // and per-row error reporting is the whole point — a bulk insert
      // would fail the entire batch on the first bad row.
      for (let i = 0; i < rows.length; i++) {
        const raw = rows[i];
        // CSV/XLSX have a header on line 1 (offset 2); JSON has none (offset 1).
        const rowNumber = i + rowNumberOffset;
        const email = String(raw['Email'] || '').trim().toLowerCase();

        if (!email) {
          skipped.push({ rowNumber, email: '(blank)', reason: 'Missing Email column' });
          continue;
        }
        const emp = empByEmail.get(email);
        if (!emp) {
          skipped.push({ rowNumber, email, reason: 'Employee not found in directory' });
          continue;
        }

        const rawStatus = (raw['Status'] || '').trim();
        if (!rawStatus || rawStatus.toLowerCase() === 'not started') {
          skipped.push({ rowNumber, email, reason: 'Status is Not Started — nothing to import' });
          continue;
        }
        const split = splitStatus(rawStatus);
        if (!split) {
          skipped.push({ rowNumber, email, reason: `Unrecognized Status: "${rawStatus}"` });
          continue;
        }

        let payload;
        if (isAnnual) {
          payload = buildAnnualRow(raw, emp.id, cycle.id, split);
        } else {
          // Validate each mid-year status field; undefined means the value
          // didn't match any allowed enum so we skip rather than silently
          // NULL it.
          const midStatuses = {
            must:   cleanMidyearStatus(raw['Must Status']),
            should: cleanMidyearStatus(raw['Should Status']),
            could:  cleanMidyearStatus(raw['Could Status']),
          };
          const badField = ['must', 'should', 'could'].find(k => midStatuses[k] === undefined);
          if (badField) {
            const label = `${badField[0].toUpperCase()}${badField.slice(1)} Status`;
            const badVal = String(raw[label] || '').trim();
            skipped.push({ rowNumber, email, reason: `Unrecognized mid-year status: "${badVal}" in ${label}` });
            continue;
          }
          payload = buildMidyearRow(raw, emp.id, cycle.id, split, midStatuses);
        }

        // Silent: per-row errors get accumulated into the summary instead
        // of firing a toast for every duplicate / FK violation.
        const { error } = await supabase.from(table).insert(payload).silent();
        if (error) {
          const reason = error.code === '23505'
            ? 'Review already exists for this employee + cycle'
            : (error.message || `Insert failed (${error.code || error.status || 'unknown'})`);
          skipped.push({ rowNumber, email, reason });
          continue;
        }
        imported++;
      }

      setImportSummary({ imported, skipped, total: rows.length });
      if (imported > 0) fetchAll();
    } catch (err) {
      console.error(err);
      flash('Import failed');
    } finally {
      setImporting(false);
    }
  };

  // ── Dashboard stats ─────────────────────────────────────────────────────────
  // Dashboard progress is keyed off the open Annual cycle. The Mid-Year
  // cycle uses a different review table and a different progress flow,
  // so don't conflate the two.
  const openCycle = cycles.find(c => c.is_open && c.type === 'Annual');
  const cycleReviews = openCycle ? annualReviews.filter(r => r.cycle_id === openCycle.id) : [];
  const totalCompleted = cycleReviews.filter(r => r.manager_status === 'completed').length;
  const totalSubmitted = cycleReviews.filter(r => r.emp_status === 'submitted' && r.manager_status !== 'completed').length;
  const totalInProgress = cycleReviews.filter(r => r.emp_status === 'in_progress').length;
  const totalNotStarted = activeEmps.length - totalCompleted - totalSubmitted - totalInProgress;
  const overallPct = activeEmps.length ? Math.round((totalCompleted / activeEmps.length) * 100) : 0;

  const deptStats = departments.map(dept => {
    const emps = activeEmps.filter(e => e.department === dept);
    const empIds = emps.map(e => e.id);
    const deptReviews = cycleReviews.filter(r => empIds.includes(r.employee_id));
    const completed = deptReviews.filter(r => r.manager_status === 'completed').length;
    const submitted = deptReviews.filter(r => r.emp_status === 'submitted' && r.manager_status !== 'completed').length;
    const inProgress = deptReviews.filter(r => r.emp_status === 'in_progress').length;
    const notStarted = emps.length - completed - submitted - inProgress;
    const pct = emps.length ? Math.round((completed / emps.length) * 100) : 0;
    return { dept, total: emps.length, completed, submitted, inProgress, notStarted, pct };
  });

  const overallStatus = emp => {
    const rev = cycleReviews.find(r => r.employee_id === emp.id);
    if (rev?.manager_status === 'completed') return 'completed';
    if (rev?.emp_status === 'submitted') return 'submitted';
    if (rev?.emp_status === 'in_progress') return 'in_progress';
    return 'not_started';
  };

  const filtered = activeEmps.filter(e => {
    if (deptFilter !== 'All' && e.department !== deptFilter) return false;
    const st = overallStatus(e);
    if (statusFilter === 'Not Started' && st !== 'not_started') return false;
    if (statusFilter === 'In Progress' && st !== 'in_progress') return false;
    if (statusFilter === 'Submitted' && st !== 'submitted') return false;
    if (statusFilter === 'Completed' && st !== 'completed') return false;
    if (search && !e.name.toLowerCase().includes(search.toLowerCase()) && !e.department.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const inputStyle = { fontSize: 12, padding: '8px 12px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: B.surfaceLight, color: B.textPrimary, fontFamily: 'inherit', width: '100%' };
  const labelStyle = { fontSize: 10, letterSpacing: '0.07em', color: B.textSecondary, textTransform: 'uppercase', display: 'block', marginBottom: 6 };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: B.black, color: B.textSecondary, fontSize: 12, letterSpacing: '0.08em' }}>LOADING...</div>;

  const Header = () => (
    <div style={{ background: B.black, borderBottom: `0.5px solid ${B.border}`, padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <img src="/logo-full.png" alt="xLight" style={{ height: 22, width: 'auto', display: 'block' }} />
        </button>
        <span style={{ width: 1, height: 16, background: B.border, margin: '0 8px' }} />
        <span style={{ fontSize: 11, color: B.textSecondary, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Admin</span>
        <button onClick={() => navigate('/')} style={{ marginLeft: 16, fontSize: 11, letterSpacing: '0.06em', padding: '6px 14px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'transparent', color: B.textSecondary, cursor: 'pointer' }}>← BACK TO PORTAL</button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {saveMsg && <span style={{ fontSize: 11, color: B.green, letterSpacing: '0.04em' }}>{saveMsg}</span>}
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: 11, color: B.textSecondary, margin: 0, letterSpacing: '0.06em' }}>PERFORMANCE MANAGEMENT</p>
          <p style={{ fontSize: 12, color: B.textPrimary, margin: '2px 0 0' }}>{admin?.name}</p>
        </div>
      </div>
    </div>
  );

  const NavTabs = () => (
    <div style={{ background: B.surface, borderBottom: `0.5px solid ${B.border}`, padding: '0 24px', display: 'flex' }}>
      {[{ id: 'dashboard', label: 'Dashboard' }, { id: 'cycles', label: 'Review Cycles' }, { id: 'people', label: 'People & Access' }, { id: 'reviews', label: 'Reviews' }, { id: 'announcements', label: 'Announcements' }, { id: 'audit', label: 'Audit Log' }, { id: 'sync-issues', label: 'Sync Issues' }].map(t => (
        <button key={t.id} onClick={() => setView(t.id)}
          style={{ fontSize: 12, letterSpacing: '0.05em', color: view === t.id ? B.textPrimary : B.textSecondary, background: 'none', border: 'none', borderBottom: view === t.id ? `1.5px solid ${B.purple}` : '1.5px solid transparent', padding: '10px 16px', cursor: 'pointer' }}>
          {t.label.toUpperCase()}
        </button>
      ))}
    </div>
  );

  // ── REVIEW CYCLES TAB ───────────────────────────────────────────────────────
  if (view === 'cycles') return (
    <div style={{ background: B.black, minHeight: '100vh', fontFamily: 'inherit' }}>
      <Header /><NavTabs />
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '1.5rem' }}>

        <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '12px 16px', marginBottom: 20 }}>
          <p style={{ fontSize: 10, letterSpacing: '0.07em', color: B.textSecondary, margin: '0 0 6px', textTransform: 'uppercase' }}>Import historical reviews</p>
          <p style={{ fontSize: 11, color: B.textMuted, margin: '0 0 10px', lineHeight: 1.5 }}>Pick a cycle, the review type, and a CSV / Excel / JSON file in the same shape as the cycle export. Rows for unknown emails are skipped, not auto-created.</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <select value={importCycleId} onChange={e => {
              const id = e.target.value;
              setImportCycleId(id);
              // Lock the type selector to the cycle's own type so it can't
              // drift and route rows to the wrong table.
              const c = cycles.find(c => c.id === id);
              if (c) setImportType(c.type);
            }} style={{ flex: 1, minWidth: 180, fontSize: 12, padding: '7px 10px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: B.surface, color: B.textPrimary, cursor: 'pointer' }}>
              <option value="">— Select cycle —</option>
              {cycles.map(c => <option key={c.id} value={c.id}>{c.label}{c.is_open ? ' (open)' : ''}</option>)}
            </select>
            <select value={importType} onChange={e => setImportType(e.target.value)} disabled={!!importCycleId}
              title={importCycleId ? 'Type is set by the selected cycle' : ''}
              style={{ fontSize: 12, padding: '7px 10px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: B.surface, color: importCycleId ? B.textMuted : B.textPrimary, cursor: importCycleId ? 'not-allowed' : 'pointer', opacity: importCycleId ? 0.6 : 1 }}>
              <option value="Annual">Annual</option>
              <option value="Mid-Year">Mid-Year</option>
            </select>
            <input ref={importInputRef} type="file" accept=".csv,.xlsx,.xls,.json,application/json,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={e => setImportFile(e.target.files?.[0] || null)}
              style={{ flex: 1, minWidth: 200, fontSize: 11, padding: '6px 8px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: B.surface, color: B.textSecondary, cursor: 'pointer' }} />
          </div>
          <button onClick={handleImportSubmit} disabled={importing || !importCycleId || !importFile}
            style={{ fontSize: 11, letterSpacing: '0.07em', padding: '8px 20px', borderRadius: 6, border: 'none', background: (importing || !importCycleId || !importFile) ? B.border : B.gold, color: (importing || !importCycleId || !importFile) ? B.textMuted : '#000', cursor: (importing || !importCycleId || !importFile) ? 'not-allowed' : 'pointer', fontWeight: 500 }}>
            {importing ? 'IMPORTING...' : '↑ IMPORT REVIEWS'}
          </button>
        </div>

        {importSummary && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
            onClick={() => setImportSummary(null)}>
            <div onClick={e => e.stopPropagation()}
              style={{ background: B.surface, border: `0.5px solid ${B.purpleDim}`, borderRadius: 10, padding: '1.5rem', width: 'min(560px, 92vw)', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
              <p style={{ fontSize: 13, fontWeight: 500, color: B.textPrimary, margin: '0 0 4px' }}>Import complete</p>
              <p style={{ fontSize: 12, color: B.textSecondary, margin: '0 0 14px' }}>
                <span style={{ color: B.green }}>{importSummary.imported} imported</span>
                {' · '}
                <span style={{ color: importSummary.skipped.length ? B.gold : B.textMuted }}>{importSummary.skipped.length} skipped</span>
                {' · '}
                <span style={{ color: B.textMuted }}>{importSummary.total} total rows</span>
              </p>
              {importSummary.skipped.length > 0 && (
                <div style={{ flex: 1, overflow: 'auto', border: `0.5px solid ${B.border}`, borderRadius: 6 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '60px 1.4fr 2fr', padding: '8px 12px', borderBottom: `0.5px solid ${B.border}`, background: B.surfaceLight }}>
                    {['Row', 'Email', 'Reason'].map(h => <span key={h} style={{ fontSize: 10, letterSpacing: '0.08em', color: B.textSecondary, textTransform: 'uppercase' }}>{h}</span>)}
                  </div>
                  {importSummary.skipped.map((s, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '60px 1.4fr 2fr', padding: '8px 12px', borderBottom: i < importSummary.skipped.length - 1 ? `0.5px solid ${B.border}` : 'none', alignItems: 'baseline', gap: 8 }}>
                      <span style={{ fontSize: 11, color: B.textMuted }}>{s.rowNumber}</span>
                      <span style={{ fontSize: 11, color: B.textSecondary, wordBreak: 'break-all' }}>{s.email}</span>
                      <span style={{ fontSize: 11, color: B.gold }}>{s.reason}</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                <button onClick={() => { setImportSummary(null); resetImportFile(); }}
                  style={{ fontSize: 12, letterSpacing: '0.06em', padding: '8px 22px', borderRadius: 6, border: 'none', background: B.purple, color: '#fff', cursor: 'pointer' }}>DISMISS</button>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, margin: 0, textTransform: 'uppercase' }}>Review cycles · {cycles.length}</p>
          {!showAddCycle && (
            <button onClick={() => setShowAddCycle(true)} style={{ fontSize: 11, letterSpacing: '0.06em', padding: '8px 18px', borderRadius: 6, border: `0.5px solid ${B.purpleDim}`, background: B.purpleDeep, color: B.purple, cursor: 'pointer' }}>+ NEW CYCLE</button>
          )}
        </div>

        {showAddCycle && (
          <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.purpleDim}`, borderRadius: 10, padding: '1.25rem', marginBottom: 24 }}>
            <p style={{ fontSize: 13, fontWeight: 500, color: B.textPrimary, marginBottom: 14 }}>New review cycle</p>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={labelStyle}>Label</label>
                <input value={newCycle.label} onChange={e => setNewCycle(p => ({ ...p, label: e.target.value }))} placeholder="e.g. Annual Review 2027" style={{ ...inputStyle, borderColor: cycleErrors.label ? B.red : B.border }} />
                {cycleErrors.label && <p style={{ fontSize: 11, color: B.red, marginTop: 4 }}>{cycleErrors.label}</p>}
              </div>
              <div>
                <label style={labelStyle}>Type</label>
                <select value={newCycle.type} onChange={e => setNewCycle(p => ({ ...p, type: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                  <option value="Annual">Annual</option>
                  <option value="Mid-Year">Mid-Year</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Year</label>
                <input type="number" value={newCycle.year} onChange={e => setNewCycle(p => ({ ...p, year: e.target.value }))} style={{ ...inputStyle, borderColor: cycleErrors.year ? B.red : B.border }} />
                {cycleErrors.year && <p style={{ fontSize: 11, color: B.red, marginTop: 4 }}>{cycleErrors.year}</p>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowAddCycle(false); setCycleErrors({}); setNewCycle({ label: '', type: 'Annual', year: new Date().getFullYear() }); }} style={{ fontSize: 12, letterSpacing: '0.06em', padding: '8px 18px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'transparent', color: B.textSecondary, cursor: 'pointer' }}>CANCEL</button>
              <button onClick={addCycle} style={{ fontSize: 12, letterSpacing: '0.06em', padding: '8px 18px', borderRadius: 6, border: 'none', background: B.purple, color: '#fff', cursor: 'pointer' }}>ADD CYCLE</button>
            </div>
            <p style={{ fontSize: 11, color: B.textMuted, marginTop: 10, lineHeight: 1.5 }}>New cycles start as <strong>Closed</strong>. Open them when ready for employees to begin their reviews.</p>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {cycles.map(c => {
            const isOpen = c.is_open;
            const isPendingToggle = confirmCycle === c.id;
            const isPendingDelete = confirmDeleteCycle === c.id;
            const isEditing = editingCycle === c.id;
            const hasReviews = cycleHasReviews(c.id);
            return (
              <div key={c.id} style={{ background: B.surfaceLight, border: `0.5px solid ${isOpen ? B.purpleDim : B.border}`, borderRadius: 8, padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: isOpen ? B.green : B.textMuted }} />
                    {isEditing ? (
                      <div style={{ display: 'flex', gap: 8, flex: 1 }}>
                        <input value={editCycleValues.label} onChange={e => setEditCycleValues(v => ({ ...v, label: e.target.value }))} style={{ ...inputStyle, flex: 1, padding: '6px 10px' }} />
                        <input type="number" value={editCycleValues.year} onChange={e => setEditCycleValues(v => ({ ...v, year: e.target.value }))} style={{ ...inputStyle, width: 90, padding: '6px 10px' }} />
                      </div>
                    ) : (
                      <div>
                        <p style={{ fontSize: 13, color: B.textPrimary, margin: 0 }}>{c.label}</p>
                        <p style={{ fontSize: 11, color: B.textSecondary, margin: '2px 0 0', letterSpacing: '0.04em' }}>
                          {c.type} · {c.year} · {isOpen ? 'Open' : 'Closed'}
                          {hasReviews && ` · ${reviewCountByCycleId.get(c.id) || 0} reviews`}
                        </p>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {isEditing ? (
                      <>
                        <button onClick={() => saveEditCycle(c.id)} style={{ fontSize: 11, letterSpacing: '0.06em', padding: '6px 14px', borderRadius: 6, border: `0.5px solid ${B.purple}`, background: B.purpleDeep, color: B.purple, cursor: 'pointer' }}>SAVE</button>
                        <button onClick={() => setEditingCycle(null)} style={{ fontSize: 11, padding: '6px 12px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'transparent', color: B.textSecondary, cursor: 'pointer' }}>CANCEL</button>
                      </>
                    ) : (
                      <>
                        {isPendingToggle && <span style={{ fontSize: 11, color: B.gold }}>Confirm {isOpen ? 'close' : 'open'}?</span>}
                        {isPendingDelete && <span style={{ fontSize: 11, color: B.red }}>Confirm delete?</span>}
                        <button onClick={() => toggleCycle(c.id)} style={{ fontSize: 11, letterSpacing: '0.06em', padding: '6px 14px', borderRadius: 6, border: `0.5px solid ${isPendingToggle ? B.gold : isOpen ? B.red : B.purpleDim}`, background: 'transparent', color: isPendingToggle ? B.gold : isOpen ? B.red : B.purple, cursor: 'pointer' }}>
                          {isPendingToggle ? 'CONFIRM' : isOpen ? 'CLOSE' : 'OPEN'}
                        </button>
                        {!isOpen && !isPendingToggle && !isPendingDelete && (
                          <>
                            <button onClick={() => startEditCycle(c)} style={{ fontSize: 11, padding: '6px 12px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'transparent', color: B.textSecondary, cursor: 'pointer' }}>EDIT</button>
                            {/* tooltip on a wrapper span — disabled buttons swallow hover events */}
                            <span title={hasReviews ? 'Cycle has reviews; cannot delete' : 'Delete cycle'} style={{ display: 'inline-block' }}>
                              <button onClick={() => deleteCycle(c.id)} disabled={hasReviews} aria-label={hasReviews ? 'Delete (disabled — cycle has reviews)' : 'Delete cycle'} style={{ fontSize: 11, padding: '6px 12px', borderRadius: 6, border: `0.5px solid ${hasReviews ? B.border : B.redDim}`, background: 'transparent', color: hasReviews ? B.textMuted : B.red, cursor: hasReviews ? 'not-allowed' : 'pointer' }}>DELETE</button>
                            </span>
                          </>
                        )}
                        {isPendingDelete && (
                          <>
                            <button onClick={() => deleteCycle(c.id)} style={{ fontSize: 11, letterSpacing: '0.06em', padding: '6px 14px', borderRadius: 6, border: `0.5px solid ${B.red}`, background: B.redDeep, color: B.red, cursor: 'pointer' }}>CONFIRM DELETE</button>
                            <button onClick={() => setConfirmDeleteCycle(null)} style={{ fontSize: 11, padding: '6px 12px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'transparent', color: B.textSecondary, cursor: 'pointer' }}>CANCEL</button>
                          </>
                        )}
                        {isPendingToggle && <button onClick={() => setConfirmCycle(null)} style={{ fontSize: 11, padding: '6px 12px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'transparent', color: B.textSecondary, cursor: 'pointer' }}>CANCEL</button>}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {cycles.length === 0 && (
            <p style={{ fontSize: 12, color: B.textMuted, textAlign: 'center', padding: '2rem' }}>No review cycles yet. Add one to get started.</p>
          )}
        </div>

        <p style={{ fontSize: 11, color: B.textMuted, marginTop: 24, lineHeight: 1.6 }}>
          <strong style={{ color: B.textSecondary }}>About cycles:</strong> Each cycle is either Annual (year-end self + manager review) or Mid-Year (mid-cycle priority check-in).
          Only one cycle of each type can be Open at a time (enforced by the database).
          Cycles with existing reviews cannot be deleted — this protects historical performance data.
          EDIT and DELETE are only available on closed cycles; close a cycle first if you need to rename or remove it.
        </p>
      </div>
    </div>
  );

  // ── REVIEWS TAB ─────────────────────────────────────────────────────────────
  // Admin "Review Editor": pick employee + cycle, edit the row directly.
  // Field set mirrors EmployeeSelfEval/ManagerReview (annual) and
  // MidYearForm/MidYearManagerReview (mid-year). Inactive employees stay
  // selectable so HR can correct historical reviews of leavers. Save is
  // an upsert: UPDATE existing row, otherwise INSERT a new one. Audit log
  // entries are written automatically by audit_annual_reviews /
  // audit_midyear_reviews triggers — nothing extra to do here.
  if (view === 'reviews') {
    const selectedCycle = cycles.find(c => c.id === revCycleId);
    const isAnnual = selectedCycle?.type === 'Annual';
    const isMid    = selectedCycle?.type === 'Mid-Year';

    // Filterable employee picker — search is over name + email so admin
    // can paste an email straight in. Sorted name-asc; inactive folks are
    // pushed to the bottom and tagged inline so they're still pickable
    // but visually de-emphasized (consistent with People & Access).
    const empOptions = [...employees].sort((a, b) => {
      if (a.active !== b.active) return a.active ? -1 : 1;
      return a.name.localeCompare(b.name);
    }).filter(e => {
      if (!revEmpSearch.trim()) return true;
      const q = revEmpSearch.toLowerCase();
      return (e.name || '').toLowerCase().includes(q) || (e.email || '').toLowerCase().includes(q);
    });
    const selectedEmp = employees.find(e => e.id === revEmployeeId);

    // Status pill colors — reuse the global statusMeta. The fallback only
    // fires for unknown values (defensive); known statuses including
    // 'pending' have explicit entries above.
    const annualStatusPill = (kind, value) => {
      const m = statusMeta[value] || { label: value, color: B.textMuted, bg: B.surfaceLight, border: B.border };
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10, letterSpacing: '0.06em', padding: '3px 10px', borderRadius: 99, background: m.bg, color: m.color, border: `0.5px solid ${m.border}`, textTransform: 'uppercase' }}>
          <span style={{ opacity: 0.7 }}>{kind}</span>{m.label}
        </span>
      );
    };

    const MIDYEAR_GOAL_STATUSES = [
      { key: 'done', label: 'Done', color: B.green },
      { key: 'in_progress', label: 'In Progress', color: B.gold },
      { key: 'not_started', label: 'Not Started', color: B.textMuted },
      { key: 'deprioritised', label: 'Deprioritised', color: B.textSecondary },
    ];

    const PRIORITY_META = [
      { key: 'must',   label: 'Must do',   tag: '#c0392b' },
      { key: 'should', label: 'Should do', tag: '#C9A84C' },
      { key: 'could',  label: 'Could do',  tag: '#1D9E75' },
    ];

    // Tiny labeled-textarea helper to keep the form readable.
    const FieldArea = ({ label, value, onChange, rows = 3, hint }) => (
      <div>
        <label style={labelStyle}>{label}</label>
        {hint && <p style={{ fontSize: 11, color: B.textMuted, margin: '0 0 6px', lineHeight: 1.5 }}>{hint}</p>}
        <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={rows}
          style={{ ...inputStyle, lineHeight: 1.6, resize: 'vertical' }} />
      </div>
    );

    return (
      <div style={{ background: B.black, minHeight: '100vh', fontFamily: 'inherit' }}>
        <Header /><NavTabs />
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '1.5rem' }}>

          <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, margin: '0 0 12px', textTransform: 'uppercase' }}>
            Review editor
          </p>
          <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '14px 16px', marginBottom: 20 }}>
            <p style={{ fontSize: 11, color: B.textMuted, margin: '0 0 12px', lineHeight: 1.5 }}>
              Pick an employee and a cycle to view or edit their review. Save writes directly to the row; if no review exists yet the save creates one. Includes inactive employees so historical reviews of leavers stay editable. All changes are recorded in the Audit Log.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.4fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Employee</label>
                <input value={revEmpSearch} onChange={e => setRevEmpSearch(e.target.value)} placeholder="Search name or email..."
                  style={{ ...inputStyle, marginBottom: 6 }} />
                <select value={revEmployeeId} onChange={e => {
                    // Synchronously drop the previous draft so a click on SAVE
                    // before the loadReview effect resolves can't write to the
                    // old row under the new selection.
                    setReviewDraft(null); setReviewExists(false); setRevLoading(true);
                    setRevEmployeeId(e.target.value);
                  }} size={6}
                  style={{ ...inputStyle, padding: '6px 8px', cursor: 'pointer', height: 'auto' }}>
                  <option value="">— Select employee —</option>
                  {empOptions.map(e => (
                    <option key={e.id} value={e.id}>
                      {e.name} ({e.email}){!e.active ? ' · inactive' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Cycle</label>
                <select value={revCycleId} onChange={e => {
                    // Same stale-draft guard as the employee picker.
                    setReviewDraft(null); setReviewExists(false); setRevLoading(true);
                    setRevCycleId(e.target.value);
                  }} style={{ ...inputStyle, cursor: 'pointer' }}>
                  <option value="">— Select cycle —</option>
                  {cycles.map(c => (
                    <option key={c.id} value={c.id}>{c.label} · {c.type}{c.is_open ? ' (open)' : ''}</option>
                  ))}
                </select>
                {selectedEmp && selectedCycle && (
                  <div style={{ marginTop: 14, padding: '10px 12px', background: B.surface, border: `0.5px solid ${B.border}`, borderRadius: 6 }}>
                    <p style={{ fontSize: 10, letterSpacing: '0.06em', color: B.textSecondary, margin: '0 0 4px', textTransform: 'uppercase' }}>Selection</p>
                    <p style={{ fontSize: 12, color: B.textPrimary, margin: 0 }}>{selectedEmp.name}</p>
                    <p style={{ fontSize: 11, color: B.textSecondary, margin: '2px 0 0' }}>{selectedCycle.label} · {selectedCycle.type}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {revLoading && (
            <p style={{ fontSize: 12, color: B.textMuted, textAlign: 'center', padding: '2rem' }}>Loading review...</p>
          )}

          {!revLoading && (!revEmployeeId || !revCycleId) && (
            <p style={{ fontSize: 12, color: B.textMuted, textAlign: 'center', padding: '2rem' }}>
              Pick an employee and a cycle above to load their review.
            </p>
          )}

          {!revLoading && reviewDraft && selectedCycle && (
            <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.purpleDim}`, borderRadius: 10, padding: '1.25rem', marginBottom: 24 }}>

              {/* Status pills + meta */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {annualStatusPill('Employee:', reviewDraft.emp_status)}
                  {annualStatusPill('Manager:', reviewDraft.manager_status)}
                </div>
                <span style={{ fontSize: 10, color: B.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  {reviewExists ? 'Existing row' : 'New — will create on save'}
                </span>
              </div>

              {/* Editable status enums */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>Employee status</label>
                  <select value={reviewDraft.emp_status} onChange={e => setRevField('emp_status', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="not_started">Not started</option>
                    <option value="in_progress">In progress</option>
                    <option value="submitted">Submitted</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Manager status</label>
                  <select value={reviewDraft.manager_status} onChange={e => setRevField('manager_status', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>

              {isAnnual && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <FieldArea label="Key achievements & learnings" value={reviewDraft.achievements}
                    onChange={v => setRevField('achievements', v)} rows={4}
                    hint="Employee self-evaluation field." />
                  <FieldArea label="Skills & development" value={reviewDraft.skills}
                    onChange={v => setRevField('skills', v)} rows={4} />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                    <FieldArea label="Must do"   value={reviewDraft.must_do}   onChange={v => setRevField('must_do', v)} />
                    <FieldArea label="Should do" value={reviewDraft.should_do} onChange={v => setRevField('should_do', v)} />
                    <FieldArea label="Could do"  value={reviewDraft.could_do}  onChange={v => setRevField('could_do', v)} />
                  </div>
                  <div>
                    <label style={labelStyle}>Manager rating</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <button type="button" onClick={() => setRevField('rating', null)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 6, border: `0.5px solid ${reviewDraft.rating == null ? B.purple : B.border}`, background: reviewDraft.rating == null ? B.purpleDeep : 'transparent', color: reviewDraft.rating == null ? B.purple : B.textSecondary, cursor: 'pointer', textAlign: 'left', fontSize: 12 }}>
                        — No rating —
                      </button>
                      {[1, 2, 3, 4, 5].map(v => {
                        const r = RATINGS[v];
                        const active = reviewDraft.rating === v;
                        return (
                          <button key={v} type="button" onClick={() => setRevField('rating', v)}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 6, border: `0.5px solid ${active ? r.color : B.border}`, background: active ? `${r.color}22` : 'transparent', color: active ? r.color : B.textSecondary, cursor: 'pointer', textAlign: 'left', fontSize: 12 }}>
                            {v} — {r.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <FieldArea label="Manager feedback" value={reviewDraft.manager_feedback}
                    onChange={v => setRevField('manager_feedback', v)} rows={5} />
                </div>
              )}

              {isMid && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, margin: 0, textTransform: 'uppercase' }}>Past priority progress</p>
                  {PRIORITY_META.map(({ key, label, tag }) => {
                    const statusKey = `${key}_status`;
                    const updateKey = `${key}_update`;
                    return (
                      <div key={key} style={{ background: B.surface, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '12px 14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                          <span style={{ fontSize: 10, padding: '2px 10px', borderRadius: 99, background: tag, color: '#fff', letterSpacing: '0.06em' }}>{label.toUpperCase()}</span>
                        </div>
                        <label style={labelStyle}>Status</label>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                          <button type="button" onClick={() => setRevField(statusKey, null)}
                            style={{ fontSize: 11, padding: '4px 10px', borderRadius: 99, border: `0.5px solid ${reviewDraft[statusKey] == null ? B.purple : B.border}`, background: reviewDraft[statusKey] == null ? B.purpleDeep : 'transparent', color: reviewDraft[statusKey] == null ? B.purple : B.textSecondary, cursor: 'pointer' }}>
                            — Not set —
                          </button>
                          {MIDYEAR_GOAL_STATUSES.map(s => {
                            const active = reviewDraft[statusKey] === s.key;
                            return (
                              <button key={s.key} type="button" onClick={() => setRevField(statusKey, s.key)}
                                style={{ fontSize: 11, padding: '4px 10px', borderRadius: 99, border: `0.5px solid ${active ? s.color : B.border}`, background: active ? `${s.color}22` : 'transparent', color: active ? s.color : B.textSecondary, cursor: 'pointer' }}>
                                {s.label}
                              </button>
                            );
                          })}
                        </div>
                        <FieldArea label="Update" value={reviewDraft[updateKey]} onChange={v => setRevField(updateKey, v)} />
                      </div>
                    );
                  })}

                  <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, margin: '8px 0 0', textTransform: 'uppercase' }}>Next 6-month priorities</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                    <FieldArea label="Next must"   value={reviewDraft.next_must}   onChange={v => setRevField('next_must', v)} />
                    <FieldArea label="Next should" value={reviewDraft.next_should} onChange={v => setRevField('next_should', v)} />
                    <FieldArea label="Next could"  value={reviewDraft.next_could}  onChange={v => setRevField('next_could', v)} />
                  </div>

                  <FieldArea label="Manager comments" value={reviewDraft.manager_comments}
                    onChange={v => setRevField('manager_comments', v)} rows={5} />
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginTop: 18 }}>
                <button onClick={() => loadReview(revEmployeeId, revCycleId)} disabled={revSaving || revLoading}
                  style={{ fontSize: 12, letterSpacing: '0.06em', padding: '8px 18px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'transparent', color: B.textSecondary, cursor: (revSaving || revLoading) ? 'not-allowed' : 'pointer' }}>
                  REVERT
                </button>
                <button onClick={saveReview} disabled={revSaving}
                  style={{ fontSize: 12, letterSpacing: '0.06em', padding: '8px 22px', borderRadius: 6, border: 'none', background: revSaving ? B.border : B.purple, color: revSaving ? B.textMuted : '#fff', cursor: revSaving ? 'not-allowed' : 'pointer' }}>
                  {revSaving ? 'SAVING...' : (reviewExists ? 'SAVE' : 'CREATE REVIEW')}
                </button>
              </div>
            </div>
          )}

          <p style={{ fontSize: 11, color: B.textMuted, marginTop: 24, lineHeight: 1.6 }}>
            <strong style={{ color: B.textSecondary }}>About this editor:</strong> changes here bypass the normal employee/manager workflow — use it for HR-led conversations, mid-cycle re-opens, and corrections. Every save is logged in the Audit Log along with the actor email.
          </p>
        </div>
      </div>
    );
  }

  // ── SYNC ISSUES TAB ─────────────────────────────────────────────────────────
  // .silent() suppresses the QueryBuilder's global red toast; on failure
  // we surface a contextual showError (red) instead of flash() (green).
  const fetchSyncIssues = async () => {
    setSyncIssuesLoading(true);
    try {
      const { data, error } = await supabase
        .from('sync_errors')
        .select('id, category, email, detail, created_at')
        .order('category')
        .order('email')
        .silent();
      if (error) throw error;
      setSyncIssues(data || []);
      setSyncIssuesLoaded(true);
    } catch (err) {
      console.error('Failed to fetch sync issues:', err);
      showError('Failed to load sync issues.');
    } finally {
      setSyncIssuesLoading(false);
    }
  };

  // ── AUDIT LOG TAB ───────────────────────────────────────────────────────────
  // List fetch: summary fields only (no old_data/new_data JSONB) to keep
  // the list view payload small. Full row is fetched on expand.
  const fetchAuditLog = async () => {
    setAuditLoading(true);
    try {
      const { data, error } = await supabase
        .from('audit_log')
        .select('id, created_at, operation, table_name, actor_email, record_id')
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      setAuditEntries(data || []);
    } catch (err) {
      console.error('Failed to fetch audit log:', err);
      flash('Failed to load audit log.');
    } finally {
      setAuditLoading(false);
    }
  };

  // Load the full row (including old_data/new_data JSONB) only when expanded.
  const loadAuditDetails = async entryId => {
    // Skip refetch if details are already loaded.
    const existing = auditEntries.find(e => e.id === entryId);
    if (existing?.old_data !== undefined || existing?.new_data !== undefined) return;

    try {
      const { data, error } = await supabase
        .from('audit_log')
        .select('old_data, new_data')
        .eq('id', entryId)
        .single();
      if (error) throw error;
      if (data) {
        setAuditEntries(prev => prev.map(e => e.id === entryId ? { ...e, ...data } : e));
      }
    } catch (err) {
      console.error('Failed to load audit details:', err);
      flash('Failed to load audit details.');
    }
  };

  if (view === 'sync-issues') {
    // Human-readable labels and a colour key for each error category. Keep
    // these in sync with the categories sync.py emits — adding a new one
    // upstream without an entry here will fall back to 'OTHER' / B.textMuted.
    const categoryMeta = {
      blank_user_type:    { label: 'BLANK USER TYPE',    color: B.red,    help: 'userType is empty in Okta — user not imported until corrected.' },
      invalid_user_type:  { label: 'INVALID USER TYPE',  color: B.red,    help: 'userType in Okta is not one of Employee/Contractor/Consultant/Intern/Advisor — user not imported until corrected.' },
      invalid_manager_id: { label: 'INVALID MANAGER ID', color: B.purple, help: "managerId isn't a valid email; manager_email stored as NULL." },
      missing_department: { label: 'MISSING DEPARTMENT', color: B.red,    help: 'No department set in Okta — user skipped, not imported.' },
      missing_email:      { label: 'MISSING EMAIL',      color: B.red,    help: 'No email or login set in Okta — user skipped, not imported.' },
    };

    return (
      <div style={{ background: B.black, minHeight: '100vh', fontFamily: 'inherit' }}>
        <Header /><NavTabs />
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, margin: 0, textTransform: 'uppercase' }}>
              Sync issues · {syncIssuesLoading ? 'loading…' : `${syncIssues.length} ${syncIssues.length === 1 ? 'issue' : 'issues'}`}
            </p>
            <button onClick={fetchSyncIssues} disabled={syncIssuesLoading} style={{ fontSize: 11, letterSpacing: '0.06em', padding: '6px 14px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'transparent', color: B.textSecondary, cursor: 'pointer' }}>REFRESH</button>
          </div>

          <p style={{ fontSize: 11, color: B.textMuted, marginBottom: 18, lineHeight: 1.6 }}>
            Issues found by the most recent Okta sync run (daily at 13:00 UTC). Fix the source profile in Okta — the next sync run drops the row from this list automatically. Hard failures (missing email or department) skip the user entirely; soft failures import with safe defaults.
          </p>

          {syncIssues.length === 0 && !syncIssuesLoading && (
            <p style={{ fontSize: 12, color: B.textMuted, textAlign: 'center', padding: '2rem' }}>No sync issues. Profile data in Okta looks clean.</p>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {syncIssues.map(issue => {
              const meta = categoryMeta[issue.category] || { label: (issue.category || 'OTHER').toUpperCase(), color: B.textMuted, help: '' };
              return (
                <div key={issue.id} style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 6, padding: '10px 14px', display: 'grid', gridTemplateColumns: '170px 1fr', alignItems: 'center', gap: 12 }}>
                  <span title={meta.help} style={{ fontSize: 10, letterSpacing: '0.06em', padding: '2px 8px', borderRadius: 4, background: `${meta.color}22`, color: meta.color, textAlign: 'center', justifySelf: 'start' }}>{meta.label}</span>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: 13, color: B.textPrimary, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{issue.email || '(no email)'}</p>
                    <p style={{ fontSize: 11, color: B.textSecondary, margin: '2px 0 0' }}>{issue.detail}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  if (view === 'audit') {
    const opColor = { INSERT: B.green, UPDATE: B.gold, DELETE: B.red };

    return (
      <div style={{ background: B.black, minHeight: '100vh', fontFamily: 'inherit' }}>
        <Header /><NavTabs />
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, margin: 0, textTransform: 'uppercase' }}>
              Audit log · {auditLoading ? 'loading…' : `${auditEntries.length} entries`}
            </p>
            <button onClick={fetchAuditLog} disabled={auditLoading} style={{ fontSize: 11, letterSpacing: '0.06em', padding: '6px 14px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'transparent', color: B.textSecondary, cursor: 'pointer' }}>REFRESH</button>
          </div>

          {auditEntries.length === 0 && !auditLoading && (
            <p style={{ fontSize: 12, color: B.textMuted, textAlign: 'center', padding: '2rem' }}>No audit entries yet. Changes to employees, reviews, and cycles are recorded automatically.</p>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {auditEntries.map(entry => {
              const expanded = auditExpanded === entry.id;
              return (
                <div key={entry.id} style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 6, overflow: 'hidden' }}>
                  <button
                    onClick={() => {
                      const next = expanded ? null : entry.id;
                      setAuditExpanded(next);
                      if (next) loadAuditDetails(next);
                    }}
                    style={{ width: '100%', display: 'grid', gridTemplateColumns: '120px 80px 1fr 200px', padding: '10px 14px', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: 12 }}>
                    <span style={{ fontSize: 11, color: B.textMuted }}>{new Date(entry.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    <span style={{ fontSize: 10, letterSpacing: '0.06em', padding: '2px 8px', borderRadius: 4, background: `${opColor[entry.operation] || B.textMuted}22`, color: opColor[entry.operation] || B.textMuted, textAlign: 'center' }}>{entry.operation}</span>
                    <span style={{ fontSize: 12, color: B.textPrimary }}>{entry.table_name}</span>
                    <span style={{ fontSize: 11, color: B.textSecondary, textAlign: 'right' }}>{entry.actor_email || 'system'}</span>
                  </button>
                  {expanded && (
                    <div style={{ padding: '0 14px 12px', borderTop: `0.5px solid ${B.border}` }}>
                      {entry.old_data && (
                        <div style={{ marginTop: 10 }}>
                          <p style={{ fontSize: 10, letterSpacing: '0.06em', color: B.textMuted, marginBottom: 4, textTransform: 'uppercase' }}>Previous</p>
                          <pre style={{ fontSize: 11, color: B.textSecondary, background: B.surface, padding: 10, borderRadius: 4, overflow: 'auto', maxHeight: 200, margin: 0 }}>{JSON.stringify(entry.old_data, null, 2)}</pre>
                        </div>
                      )}
                      {entry.new_data && (
                        <div style={{ marginTop: 10 }}>
                          <p style={{ fontSize: 10, letterSpacing: '0.06em', color: B.textMuted, marginBottom: 4, textTransform: 'uppercase' }}>New</p>
                          <pre style={{ fontSize: 11, color: B.textSecondary, background: B.surface, padding: 10, borderRadius: 4, overflow: 'auto', maxHeight: 200, margin: 0 }}>{JSON.stringify(entry.new_data, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── ANNOUNCEMENTS TAB ───────────────────────────────────────────────────────
  // List is already pinned-first / newest-first thanks to the fetchAll
  // ordering; render it as-is so the admin view matches HRPortal exactly.
  if (view === 'announcements') return (
    <div style={{ background: B.black, minHeight: '100vh', fontFamily: 'inherit' }}>
      <Header /><NavTabs />
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, margin: 0, textTransform: 'uppercase' }}>Announcements · {announcements.length}</p>
          {!showAnnForm && (
            <button onClick={startNewAnn} style={{ fontSize: 11, letterSpacing: '0.06em', padding: '8px 18px', borderRadius: 6, border: `0.5px solid ${B.purpleDim}`, background: B.purpleDeep, color: B.purple, cursor: 'pointer' }}>+ NEW ANNOUNCEMENT</button>
          )}
        </div>

        {showAnnForm && (
          <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.purpleDim}`, borderRadius: 10, padding: '1.25rem', marginBottom: 24 }}>
            <p style={{ fontSize: 13, fontWeight: 500, color: B.textPrimary, marginBottom: 14 }}>{editingAnn ? 'Edit announcement' : 'New announcement'}</p>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={labelStyle}>Title</label>
                <input value={annDraft.title} onChange={e => setAnnDraft(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Mid-year reviews open Monday" style={{ ...inputStyle, borderColor: annErrors.title ? B.red : B.border }} />
                {annErrors.title && <p style={{ fontSize: 11, color: B.red, marginTop: 4 }}>{annErrors.title}</p>}
              </div>
              <div>
                <label style={labelStyle}>Tag</label>
                <select value={annDraft.tag} onChange={e => setAnnDraft(p => ({ ...p, tag: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer', borderColor: annErrors.tag ? B.red : B.border }}>
                  {ANN_TAG_NAMES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                {annErrors.tag && <p style={{ fontSize: 11, color: B.red, marginTop: 4 }}>{annErrors.tag}</p>}
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Body</label>
              <textarea value={annDraft.body} onChange={e => setAnnDraft(p => ({ ...p, body: e.target.value }))} rows={4} placeholder="What do you want everyone to know?" style={{ ...inputStyle, borderColor: annErrors.body ? B.red : B.border, resize: 'vertical', lineHeight: 1.5 }} />
              {annErrors.body && <p style={{ fontSize: 11, color: B.red, marginTop: 4 }}>{annErrors.body}</p>}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: B.textSecondary, cursor: 'pointer', marginBottom: 14 }}>
              <input type="checkbox" checked={annDraft.pinned} onChange={e => setAnnDraft(p => ({ ...p, pinned: e.target.checked }))} style={{ cursor: 'pointer' }} />
              Pin to top of HRPortal
            </label>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={cancelAnnForm} style={{ fontSize: 12, letterSpacing: '0.06em', padding: '8px 18px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'transparent', color: B.textSecondary, cursor: 'pointer' }}>CANCEL</button>
              <button onClick={saveAnn} style={{ fontSize: 12, letterSpacing: '0.06em', padding: '8px 22px', borderRadius: 6, border: 'none', background: B.purple, color: '#fff', cursor: 'pointer' }}>{editingAnn ? 'SAVE' : 'PUBLISH'}</button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {announcements.length === 0 && !showAnnForm && (
            <p style={{ fontSize: 12, color: B.textMuted, textAlign: 'center', padding: '2rem' }}>No announcements yet. Click <strong>NEW ANNOUNCEMENT</strong> to publish your first.</p>
          )}
          {announcements.map(a => {
            const isConfirmDelete = confirmDeleteAnn === a.id;
            const isEditingThis = editingAnn === a.id && showAnnForm;
            const preview = a.body.length > 100 ? `${a.body.slice(0, 100).trim()}…` : a.body;
            return (
              <div key={a.id} style={{ background: B.surfaceLight, border: `0.5px solid ${a.pinned ? B.purpleDim : B.border}`, borderRadius: 8, padding: '14px 16px', opacity: isEditingThis ? 0.5 : 1, display: 'flex', gap: 14 }}>
                <div style={{ width: 2, background: annTagColor(a.tag), borderRadius: 99, alignSelf: 'stretch', flexShrink: 0, opacity: 0.7 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 10, letterSpacing: '0.06em', padding: '1px 8px', borderRadius: 99, color: annTagColor(a.tag), border: `0.5px solid ${annTagColor(a.tag)}`, opacity: 0.85 }}>{a.tag.toUpperCase()}</span>
                    {a.pinned && <span style={{ fontSize: 10, color: B.purple }}>📌 Pinned</span>}
                    <span style={{ fontSize: 11, color: B.textMuted }}>{new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                  <p style={{ fontSize: 13, fontWeight: 500, color: B.textPrimary, margin: '0 0 4px' }}>{a.title}</p>
                  <p style={{ fontSize: 12, color: B.textSecondary, lineHeight: 1.5, margin: 0 }}>{preview}</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                  {isConfirmDelete ? (
                    <>
                      <button onClick={() => deleteAnn(a.id)} disabled={showAnnForm} style={{ fontSize: 10, letterSpacing: '0.05em', padding: '4px 10px', borderRadius: 6, border: `0.5px solid ${showAnnForm ? B.border : B.red}`, background: showAnnForm ? 'transparent' : B.redDeep, color: showAnnForm ? B.textMuted : B.red, cursor: showAnnForm ? 'not-allowed' : 'pointer' }}>CONFIRM DELETE</button>
                      <button onClick={() => setConfirmDeleteAnn(null)} aria-label="Cancel delete" style={{ fontSize: 10, padding: '4px 8px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'transparent', color: B.textSecondary, cursor: 'pointer' }}>✕</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => startEditAnn(a)} disabled={showAnnForm} style={{ fontSize: 10, letterSpacing: '0.05em', padding: '4px 12px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'transparent', color: showAnnForm ? B.textMuted : B.textSecondary, cursor: showAnnForm ? 'not-allowed' : 'pointer' }}>EDIT</button>
                      <button onClick={() => setConfirmDeleteAnn(a.id)} disabled={showAnnForm} style={{ fontSize: 10, letterSpacing: '0.05em', padding: '4px 12px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'transparent', color: showAnnForm ? B.textMuted : B.textSecondary, cursor: showAnnForm ? 'not-allowed' : 'pointer' }}>DELETE</button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p style={{ fontSize: 11, color: B.textMuted, marginTop: 24, lineHeight: 1.6 }}>
          <strong style={{ color: B.textSecondary }}>About announcements:</strong> Anything published here shows up immediately on every employee's HRPortal home page,
          sorted with pinned items first then newest first. Tags are visual only — they color the left rail and pill on the card.
          Deletes are permanent; the audit log does not record announcement changes.
        </p>
      </div>
    </div>
  );

  // ── PEOPLE & ACCESS TAB ─────────────────────────────────────────────────────
  // People & Access is read-only for everything except role and review
  // manager. Employees, departments, the Okta manager, and active state
  // all come from the daily Okta sync — admins can't reliably edit those
  // here. Role is myhr-internal, and review_manager_email is the HR-owned
  // override for who reviews this employee. The sync never writes that
  // column (it's NULL by default); this UI is the only writer. The
  // effective reviewer at read time is coalesce(review_manager_email,
  // manager_email) — see effective_manager_email() in db/schema.sql.
  if (view === 'people') return (
    <div style={{ background: B.black, minHeight: '100vh', fontFamily: 'inherit' }}>
      <Header /><NavTabs />
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, textTransform: 'uppercase', margin: 0 }}>Active employees · {activeEmps.length}</p>
          <input value={searchPeople} onChange={e => setSearchPeople(e.target.value)} placeholder="Search..." style={{ fontSize: 12, padding: '6px 12px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: B.surfaceLight, color: B.textPrimary, fontFamily: 'inherit', width: 160 }} />
        </div>

        <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.1fr 1.4fr 1.4fr 0.8fr 0.9fr', padding: '10px 16px', borderBottom: `0.5px solid ${B.border}`, gap: 4 }}>
            {['Employee', 'Department', 'Manager', 'Review Manager', 'Role', 'Access'].map(h => <span key={h} style={{ fontSize: 10, letterSpacing: '0.08em', color: B.textSecondary, textTransform: 'uppercase' }}>{h}</span>)}
          </div>
          {activeEmps.filter(e => !searchPeople || e.name.toLowerCase().includes(searchPeople.toLowerCase()) || e.department?.toLowerCase().includes(searchPeople.toLowerCase())).map((emp, i, arr) => {
            const rm = roleMeta[emp.role];
            const overrideSet = !!emp.review_manager_email;
            const reviewerEmail = emp.review_manager_email || emp.manager_email || '';
            return (
              <div key={emp.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1.1fr 1.4fr 1.4fr 0.8fr 0.9fr', padding: '12px 16px', borderBottom: i < arr.length - 1 ? `0.5px solid ${B.border}` : 'none', alignItems: 'center', gap: 4 }}
                onMouseEnter={e => e.currentTarget.style.background = B.surface}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: '50%', background: B.purpleDeep, border: `1px solid ${B.purpleDim}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: B.purple, flexShrink: 0 }}>{initials(emp.name)}</div>
                  <div>
                    <p style={{ fontSize: 13, color: B.textPrimary, margin: 0 }}>{emp.name}</p>
                    <p style={{ fontSize: 10, color: B.textMuted, margin: 0 }}>{emp.email}</p>
                  </div>
                </div>
                <span style={{ fontSize: 12, color: B.textSecondary }}>{emp.department}</span>
                <span style={{ fontSize: 12, color: emp.manager_name ? B.textSecondary : B.textMuted, fontStyle: emp.manager_name ? 'normal' : 'italic' }}>{emp.manager_name || 'No manager'}</span>
                {editingReviewMgr === emp.id ? (
                  <select autoFocus defaultValue={emp.review_manager_email || ''}
                    onChange={e => updateReviewManager(emp.id, e.target.value)}
                    onBlur={() => setEditingReviewMgr(null)}
                    style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: `0.5px solid ${B.purpleDim}`, background: B.surfaceLight, color: B.textPrimary, cursor: 'pointer', width: '100%' }}>
                    <option value="">(default — Okta manager)</option>
                    {reviewerOptions.map(r => <option key={r.id} value={r.email}>{r.name}</option>)}
                  </select>
                ) : (
                  <button onClick={() => setEditingReviewMgr(emp.id)}
                    title={overrideSet ? 'HR override' : 'Defaults to Okta manager — click to override'}
                    style={{ fontSize: 12, padding: 0, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
                             color: overrideSet ? B.textSecondary : B.textMuted,
                             fontStyle: overrideSet ? 'normal' : 'italic' }}>
                    {reviewerEmail || 'No manager'}
                  </button>
                )}
                <span style={{ fontSize: 10, letterSpacing: '0.05em', padding: '2px 8px', borderRadius: 99, background: rm.bg, color: rm.color, border: `0.5px solid ${rm.border}`, display: 'inline-block' }}>{emp.role.toUpperCase()}</span>
                {editingRole === emp.id ? (
                  <select autoFocus defaultValue={emp.role}
                    onChange={e => updateRole(emp.id, e.target.value)}
                    onBlur={() => setEditingRole(null)}
                    style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: `0.5px solid ${B.purpleDim}`, background: B.surfaceLight, color: B.textPrimary, cursor: 'pointer', width: '100%' }}>
                    {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                ) : (
                  <button onClick={() => setEditingRole(emp.id)} style={{ fontSize: 10, letterSpacing: '0.05em', padding: '4px 10px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'transparent', color: B.textSecondary, cursor: 'pointer' }}>CHANGE</button>
                )}
              </div>
            );
          })}
        </div>

        <button onClick={() => setShowInactive(p => !p)} style={{ fontSize: 11, letterSpacing: '0.06em', color: B.textMuted, background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: showInactive ? 12 : 0 }}>
          {showInactive ? '▲ Hide' : '▼ Show'} deactivated employees ({inactiveEmps.length})
        </button>
        {showInactive && inactiveEmps.length > 0 && (
          <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, overflow: 'hidden', opacity: 0.6, marginTop: 8 }}>
            {inactiveEmps.map((emp, i) => (
              <div key={emp.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1.1fr 1.4fr 1.4fr 0.8fr 0.9fr', padding: '12px 16px', borderBottom: i < inactiveEmps.length - 1 ? `0.5px solid ${B.border}` : 'none', alignItems: 'center', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: '50%', background: B.surface, border: `1px solid ${B.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: B.textMuted, flexShrink: 0 }}>{initials(emp.name)}</div>
                  <span style={{ fontSize: 13, color: B.textMuted, textDecoration: 'line-through' }}>{emp.name}</span>
                </div>
                <span style={{ fontSize: 12, color: B.textMuted }}>{emp.department}</span>
                <span style={{ fontSize: 12, color: B.textMuted }}>{emp.manager_name}</span>
                <span style={{ fontSize: 12, color: B.textMuted }}>{emp.review_manager_email || emp.manager_email || ''}</span>
                <span style={{ fontSize: 12, color: B.textMuted }}>{emp.role}</span>
                <span />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // ── DASHBOARD TAB ───────────────────────────────────────────────────────────
  return (
    <div style={{ background: B.black, minHeight: '100vh', fontFamily: 'inherit' }}>
      <Header /><NavTabs />
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, margin: 0, textTransform: 'uppercase' }}>
            Active annual cycle: {openCycle ? openCycle.label : 'None open'}
          </p>
          <button onClick={() => setView('cycles')} style={{ fontSize: 11, letterSpacing: '0.06em', padding: '6px 14px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'transparent', color: B.textSecondary, cursor: 'pointer' }}>
            MANAGE CYCLES →
          </button>
        </div>

        {!openCycle && (
          <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '1.5rem', textAlign: 'center', marginBottom: 28 }}>
            <p style={{ fontSize: 13, color: B.textSecondary, margin: 0, marginBottom: 8 }}>No annual review cycle is currently open.</p>
            <p style={{ fontSize: 12, color: B.textMuted, margin: 0 }}>This dashboard tracks annual review progress. Open an Annual cycle from the <strong style={{ color: B.textSecondary }}>Review Cycles</strong> tab to start collecting reviews. (Mid-year cycles run independently from the Manager Portal.)</p>
          </div>
        )}

        {openCycle && (
          <>
            <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, marginBottom: 12, textTransform: 'uppercase' }}>Overall progress · {openCycle.label}</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 10, marginBottom: 20 }}>
              {[{ label: 'Not Started', val: totalNotStarted, color: B.textMuted }, { label: 'In Progress', val: totalInProgress, color: B.gold }, { label: 'Submitted', val: totalSubmitted, color: B.purple }, { label: 'Completed', val: totalCompleted, color: B.green }].map(({ label, val, color }) => (
                <div key={label} style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '1rem' }}>
                  <p style={{ fontSize: 10, letterSpacing: '0.07em', color: B.textSecondary, marginBottom: 8, textTransform: 'uppercase' }}>{label}</p>
                  <p style={{ fontSize: 22, color, margin: 0 }}>{val}</p>
                </div>
              ))}
            </div>
            <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '14px 16px', marginBottom: 28 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: B.textSecondary, letterSpacing: '0.05em' }}>OVERALL COMPLETION</span>
                <span style={{ fontSize: 11, color: B.purple }}>{overallPct}% · {totalCompleted} of {activeEmps.length} reviews complete</span>
              </div>
              <div style={{ height: 4, background: B.border, borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ width: `${overallPct}%`, height: '100%', background: B.purple, borderRadius: 99 }} />
              </div>
            </div>

            <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, marginBottom: 12, textTransform: 'uppercase' }}>Progress by department</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 28 }}>
              {deptStats.map(({ dept, completed, submitted, inProgress, notStarted, pct }) => (
                <div key={dept} style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <span style={{ fontSize: 13, color: B.textPrimary, minWidth: 140 }}>{dept}</span>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {notStarted > 0 && <span style={{ fontSize: 10, color: B.textMuted }}>{notStarted} not started</span>}
                        {inProgress > 0 && <span style={{ fontSize: 10, color: B.gold }}>{inProgress} in progress</span>}
                        {submitted > 0 && <span style={{ fontSize: 10, color: B.purple }}>{submitted} submitted</span>}
                        {completed > 0 && <span style={{ fontSize: 10, color: B.green }}>{completed} completed</span>}
                      </div>
                    </div>
                    <span style={{ fontSize: 12, color: pct === 100 ? B.green : pct > 50 ? B.purple : B.gold }}>{pct}%</span>
                  </div>
                  <div style={{ height: 3, background: B.border, borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? B.green : B.purple, borderRadius: 99 }} />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12, gap: 16, flexWrap: 'wrap' }}>
          <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, textTransform: 'uppercase', margin: 0 }}>All employees</p>
          <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '10px 16px', minWidth: 320 }}>
            <p style={{ fontSize: 10, letterSpacing: '0.07em', color: B.textSecondary, margin: '0 0 6px', textTransform: 'uppercase' }}>Export review data</p>
            <p style={{ fontSize: 11, color: B.textMuted, margin: '0 0 10px', lineHeight: 1.5 }}>Pick a cycle and a format. Export includes status, rating, and all review answers.</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              <select value={exportCycleId} onChange={e => setExportCycleId(e.target.value)} style={{ flex: 1, minWidth: 180, fontSize: 12, padding: '7px 10px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: B.surface, color: B.textPrimary, cursor: 'pointer' }}>
                <option value="">— Select cycle —</option>
                {cycles.map(c => <option key={c.id} value={c.id}>{c.label}{c.is_open ? ' (open)' : ''}</option>)}
              </select>
              <select value={exportFormat} onChange={e => setExportFormat(e.target.value)} style={{ fontSize: 12, padding: '7px 10px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: B.surface, color: B.textPrimary, cursor: 'pointer' }}>
                <option value="csv">CSV</option>
                <option value="xlsx">Excel (.xlsx)</option>
                <option value="json">JSON</option>
              </select>
            </div>
            <button onClick={handleExportSubmit} disabled={exporting || !exportCycleId} style={{ fontSize: 11, letterSpacing: '0.07em', padding: '8px 20px', borderRadius: 6, border: 'none', background: (exporting || !exportCycleId) ? B.border : B.gold, color: (exporting || !exportCycleId) ? B.textMuted : '#000', cursor: (exporting || !exportCycleId) ? 'not-allowed' : 'pointer', fontWeight: 500 }}>
              {exporting ? 'PREPARING...' : '↓ EXPORT'}
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or department..." style={{ flex: 1, minWidth: 160, fontSize: 12, padding: '7px 12px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: B.surfaceLight, color: B.textPrimary, fontFamily: 'inherit' }} />
          <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)} style={{ fontSize: 12, padding: '7px 12px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: B.surfaceLight, color: B.textPrimary, cursor: 'pointer' }}>
            {['All', ...departments].map(d => <option key={d} value={d}>{d === 'All' ? 'All Departments' : d}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ fontSize: 12, padding: '7px 12px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: B.surfaceLight, color: B.textPrimary, cursor: 'pointer' }}>
            {['All', 'Not Started', 'In Progress', 'Submitted', 'Completed'].map(s => <option key={s} value={s}>{s === 'All' ? 'All Statuses' : s}</option>)}
          </select>
        </div>

        <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.5fr 1fr 1.2fr', padding: '10px 16px', borderBottom: `0.5px solid ${B.border}` }}>
            {['Employee', 'Department', 'Manager', 'Status', 'Rating'].map(h => <span key={h} style={{ fontSize: 10, letterSpacing: '0.08em', color: B.textSecondary, textTransform: 'uppercase' }}>{h}</span>)}
          </div>
          {filtered.length === 0 && <div style={{ padding: '2rem', textAlign: 'center' }}><p style={{ fontSize: 13, color: B.textMuted }}>No employees match the current filters.</p></div>}
          {filtered.map((emp, i) => {
            const st = statusMeta[overallStatus(emp)];
            const rev = cycleReviews.find(r => r.employee_id === emp.id);
            const rObj = rev?.rating ? RATINGS[rev.rating] : null;
            return (
              <div key={emp.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.5fr 1fr 1.2fr', padding: '12px 16px', borderBottom: i < filtered.length - 1 ? `0.5px solid ${B.border}` : 'none', alignItems: 'center' }}
                onMouseEnter={e => e.currentTarget.style.background = B.surface}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: '50%', background: B.purpleDeep, border: `1px solid ${B.purpleDim}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: B.purple, flexShrink: 0 }}>{initials(emp.name)}</div>
                  <span style={{ fontSize: 13, color: B.textPrimary }}>{emp.name}</span>
                </div>
                <span style={{ fontSize: 12, color: B.textSecondary }}>{emp.department}</span>
                <span style={{ fontSize: 12, color: B.textSecondary }}>{emp.manager_name}</span>
                <span style={{ fontSize: 10, letterSpacing: '0.05em', padding: '2px 10px', borderRadius: 99, background: st.bg, color: st.color, border: `0.5px solid ${st.border}`, display: 'inline-block', whiteSpace: 'nowrap' }}>{st.label.toUpperCase()}</span>
                {rObj ? <span style={{ fontSize: 12, color: rObj.color }}>{rObj.label}</span> : <span style={{ fontSize: 12, color: B.textMuted }}>—</span>}
              </div>
            );
          })}
        </div>
        <p style={{ fontSize: 11, color: B.textMuted, marginTop: 10 }}>Showing {filtered.length} of {activeEmps.length} active employees</p>
      </div>
    </div>
  );
}
